import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { listings } from "../../mocks/listings";
import type { Listing } from "../../mocks/listings";

function formatPrice(price: number) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(price);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Gallery({ photos, title }: { photos: string[]; title: string }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  return (
    <div className="space-y-3">
      {/* Main Image */}
      <div
        className="relative w-full h-[300px] md:h-[420px] rounded-xl overflow-hidden cursor-pointer group"
        onClick={() => setLightbox(true)}
      >
        <img
          src={photos[active]}
          alt={`${title} - Fotoğraf ${active + 1}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-surface-900/70 text-white text-xs rounded-md">
          {active + 1} / {photos.length}
        </div>
        <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-surface-600 opacity-0 group-hover:opacity-100 transition-opacity">
          <i className="ri-fullscreen-line" />
        </div>
      </div>

      {/* Thumbnails */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {photos.map((photo, idx) => (
          <button
            key={idx}
            onClick={() => setActive(idx)}
            className={`relative w-20 h-14 md:w-24 md:h-16 rounded-lg overflow-hidden shrink-0 border-2 transition-all ${
              idx === active
                ? "border-primary-500 ring-2 ring-primary-100"
                : "border-transparent opacity-70 hover:opacity-100"
            }`}
          >
            <img
              src={photo}
              alt={`${title} thumbnail ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-surface-900/95 z-50 flex items-center justify-center"
          onClick={() => setLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={() => setLightbox(false)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setActive((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
            }}
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setActive((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
            }}
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <img
            src={photos[active]}
            alt={title}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white/10 text-white text-sm rounded-full">
            {active + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
        <i className={`${icon} text-base`} />
      </div>
      <div>
        <p className="text-xs text-surface-400 mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-surface-800">{value}</p>
      </div>
    </div>
  );
}

function EIDSBadge({ status }: { status: string }) {
  const styles = {
    Beklemede: "bg-accent-50 text-accent-600 border-accent-200",
    Doğrulandı: "bg-emerald-50 text-emerald-600 border-emerald-200",
    Reddedildi: "bg-red-50 text-red-600 border-red-200",
  };
  const icons = {
    Beklemede: "ri-time-line",
    Doğrulandı: "ri-check-double-line",
    Reddedildi: "ri-close-circle-line",
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${styles[status as keyof typeof styles] || styles.Beklemede}`}
    >
      <i className={icons[status as keyof typeof icons] || icons.Beklemede} />
      EİDS: {status}
    </div>
  );
}

function SimilarListings({ currentId }: { currentId: string }) {
  const similar = listings
    .filter((l) => l.id !== currentId)
    .slice(0, 3);

  return (
    <section className="bg-surface-50 py-12 md:py-16">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-surface-900 mb-6">
            Benzer İlanlar
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {similar.map((listing) => (
              <Link
                key={listing.id}
                to={`/ilan/${listing.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-surface-100 hover:border-surface-200 hover:shadow-medium transition-all"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={listing.photos[0]}
                    alt={listing.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-xs text-surface-500 mb-1.5">
                    <i className="ri-map-pin-line" />
                    {listing.city}, {listing.district}
                  </div>
                  <h3 className="text-sm font-semibold text-surface-900 mb-3 line-clamp-2">
                    {listing.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary-600">
                      {formatPrice(listing.price)}
                    </span>
                    <span className="text-xs text-surface-400">
                      {listing.area_m2.toLocaleString("tr-TR")} m²
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<Listing | null>(null);

  useEffect(() => {
    const found = listings.find((l) => l.id === id);
    if (found) setListing(found);
    window.scrollTo(0, 0);
  }, [id]);

  if (!listing) {
    return (
      <div className="min-h-screen bg-surface-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-surface-100 text-surface-400 mx-auto mb-4">
            <i className="ri-file-search-line text-2xl" />
          </div>
          <h2 className="text-xl font-bold text-surface-900 mb-2">
            İlan Bulunamadı
          </h2>
          <p className="text-sm text-surface-500 mb-6">
            Aradığınız ilan mevcut değil veya kaldırılmış olabilir.
          </p>
          <Link
            to="/ilanlar"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors"
          >
            İlanlara Dön
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const whatsappMessage = encodeURIComponent(
    `Merhaba, Arsam.com'daki "${listing.title}" ilanınızla ilgili bilgi almak istiyorum. İlan No: ${listing.id}`,
  );
  const whatsappLink = `https://wa.me/?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-surface-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-8">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-surface-500 mb-5">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                Anasayfa
              </Link>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs" />
              </div>
              <Link
                to="/ilanlar"
                className="hover:text-primary-500 transition-colors"
              >
                İlanlar
              </Link>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs" />
              </div>
              <span className="text-surface-700 truncate max-w-[200px]">
                {listing.title}
              </span>
            </nav>

            {/* Title */}
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-surface-900 mb-2 leading-snug">
              {listing.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-surface-500 mb-6">
              <span className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-map-pin-line" />
                </div>
                {listing.city}, {listing.district}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-calendar-line" />
                </div>
                {formatDate(listing.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line" />
                </div>
                İlan No: {listing.id}
              </span>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left: Gallery + Details */}
              <div className="flex-1 min-w-0">
                <Gallery photos={listing.photos} title={listing.title} />

                {/* Description */}
                <div className="mt-6 bg-white rounded-xl border border-surface-200 p-5 md:p-6">
                  <h2 className="text-base font-bold text-surface-900 mb-3">
                    Açıklama
                  </h2>
                  <p className="text-sm text-surface-600 leading-relaxed whitespace-pre-line">
                    {listing.description}
                  </p>
                </div>

                {/* Investment Potential */}
                {listing.investment_potential && (
                  <div className="mt-4 bg-accent-50 rounded-xl border border-accent-200 p-5 md:p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-card text-accent-500 shrink-0">
                        <i className="ri-lightbulb-flash-line text-lg" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-surface-900 mb-1">
                          Yatırım Fırsatı
                        </h3>
                        <p className="text-sm text-surface-600 leading-relaxed">
                          {listing.investment_potential}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* EİDS */}
                <div className="mt-4 bg-white rounded-xl border border-surface-200 p-5">
                  <h2 className="text-base font-bold text-surface-900 mb-3">
                    EİDS Doğrulama
                  </h2>
                  <div className="flex flex-wrap items-center gap-3">
                    <EIDSBadge status={listing.eids_status} />
                    <span className="text-xs text-surface-500">
                      Taşınmaz Yetki Numarası: {listing.eids_number}
                    </span>
                  </div>
                  <p className="text-xs text-surface-400 mt-2">
                    Elektronik İlan Doğrulama Sistemi (EİDS) ile tapu
                    kayıtları üzerinden yetki doğrulaması yapılmıştır.
                  </p>
                </div>
              </div>

              {/* Right: Sidebar */}
              <div className="w-full lg:w-[340px] shrink-0 space-y-4">
                {/* Price Card */}
                <div className="bg-white rounded-xl border border-surface-200 p-5">
                  <p className="text-xs text-surface-400 mb-1">Fiyat</p>
                  <p className="text-2xl md:text-3xl font-bold text-primary-600 mb-4">
                    {formatPrice(listing.price)}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-surface-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-surface-800">
                        {listing.area_m2.toLocaleString("tr-TR")}
                      </p>
                      <p className="text-xs text-surface-500">m²</p>
                    </div>
                    <div className="bg-surface-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-surface-800">
                        {Math.round(listing.price / listing.area_m2).toLocaleString("tr-TR")}
                      </p>
                      <p className="text-xs text-surface-500">₺/m²</p>
                    </div>
                  </div>

                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-emerald-500 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 transition-colors"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-base" />
                    </div>
                    WhatsApp ile İletişime Geç
                  </a>
                </div>

                {/* Details Card */}
                <div className="bg-white rounded-xl border border-surface-200 p-5 space-y-4">
                  <h3 className="text-sm font-bold text-surface-900">
                    İlan Detayları
                  </h3>
                  <InfoRow
                    icon="ri-shape-line"
                    label="Alan"
                    value={`${listing.area_m2.toLocaleString("tr-TR")} m²`}
                  />
                  <InfoRow
                    icon="ri-file-list-line"
                    label="İmar Durumu"
                    value={listing.zoning_status}
                  />
                  <InfoRow
                    icon="ri-shield-check-line"
                    label="Tapu Durumu"
                    value={listing.deed_status}
                  />
                  <InfoRow
                    icon="ri-map-pin-line"
                    label="Konum"
                    value={`${listing.city}, ${listing.district}`}
                  />
                  {listing.swap && (
                    <InfoRow
                      icon="ri-exchange-line"
                      label="Takas"
                      value="Takas Olur"
                    />
                  )}
                </div>

                {/* Contact Card */}
                <div className="bg-white rounded-xl border border-surface-200 p-5">
                  <h3 className="text-sm font-bold text-surface-900 mb-3">
                    İlan Sahibi
                  </h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                      <i className="ri-user-line text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-surface-800">
                        {listing.user_name}
                      </p>
                      <p className="text-xs text-surface-500">
                        {listing.user_type}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-emerald-200 text-emerald-600 text-sm font-medium rounded-lg hover:bg-emerald-50 transition-colors"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-whatsapp-line" />
                      </div>
                      WhatsApp Mesajı Gönder
                    </a>
                    <a
                      href={`tel:+905001234567`}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-surface-200 text-surface-600 text-sm font-medium rounded-lg hover:bg-surface-50 transition-colors"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-phone-line" />
                      </div>
                      Telefon ile Ara
                    </a>
                  </div>
                </div>

                {/* Safety Tips */}
                <div className="bg-primary-50 rounded-xl border border-primary-100 p-4">
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0 mt-0.5">
                      <i className="ri-shield-star-line" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-primary-700 mb-1">
                        Güvenlik İpuçları
                      </h4>
                      <p className="text-xs text-primary-600 leading-relaxed">
                        Tapu bilgilerini mutlaka doğrulayın. EİDS onaylı
                        ilanları tercih edin. Karşılıklı tapu sorgusu yaptırın.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SimilarListings currentId={listing.id} />
      <Footer />
    </div>
  );
}